/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Clock, Calendar, Plus, Minus, AlertCircle, Sparkles, Timer } from 'lucide-react';

const PRESETS = [
  { id: '1h', label: '1 Hour', unit: 'hours', value: 1 },
  { id: '6h', label: '6 Hours', unit: 'hours', value: 6 },
  { id: '12h', label: '12 Hours', unit: 'hours', value: 12 },
  { id: '1d', label: '1 Day', unit: 'days', value: 1 },
  { id: '3d', label: '3 Days', unit: 'days', value: 3 },
  { id: '7d', label: '7 Days', unit: 'days', value: 7 },
  { id: 'custom', label: 'Custom', unit: 'custom', value: null },
];

const UNIT_CONFIG = {
  hours: { label: 'Hours', singular: 'hour', max: 720, step: 1, defaultVal: 1, min: 1 },
  days: { label: 'Days', singular: 'day', max: 365, step: 1, defaultVal: 1, min: 1 },
};

const pad = (n) => String(n).padStart(2, '0');

export const getMinDateTimeLocal = (offsetMs = 60 * 1000) => {
  const d = new Date(Date.now() + offsetMs);
  d.setSeconds(0, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatFutureRelative = (targetDate) => {
  if (!targetDate || isNaN(targetDate.getTime())) return '';
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs <= 0) return 'Expired';
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 60) {
    return `in ${diffMinutes} min${diffMinutes === 1 ? '' : 's'}`;
  }
  const diffHours = Math.round(diffMs / 3600000);
  if (diffHours < 24) {
    return `in ${diffHours} hour${diffHours === 1 ? '' : 's'}`;
  }
  const diffDays = Math.round(diffMs / 86400000);
  if (diffDays < 30) {
    return `in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
  }
  const diffMonths = Math.round(diffDays / 30);
  return `in ${diffMonths} month${diffMonths === 1 ? '' : 's'}`;
};

const formatDateTimeLocal = (date) => {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

function deriveInitialState(initialExpiresAt, initialCreatedAt) {
  const defaults = {
    activeTab: 'preset',
    timeUnit: 'days',
    timeValue: 3,
    customDateTime: formatDateTimeLocal(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)),
    activePresetId: '3d',
  };

  if (!initialExpiresAt || !initialCreatedAt) return defaults;

  const expiresAt = new Date(initialExpiresAt);
  const createdAt = new Date(initialCreatedAt);
  if (isNaN(expiresAt.getTime()) || isNaN(createdAt.getTime())) return defaults;

  const durationMs = expiresAt.getTime() - createdAt.getTime();
  let timeUnit = null;
  let timeValue = null;

  if (durationMs > 0 && durationMs % 86400000 === 0) {
    timeUnit = 'days';
    timeValue = durationMs / 86400000;
  } else if (durationMs > 0 && durationMs % 3600000 === 0) {
    timeUnit = 'hours';
    timeValue = durationMs / 3600000;
  }

  if (timeUnit) {
    const matchingPreset = PRESETS.find((p) => p.unit === timeUnit && p.value === timeValue);
    return {
      activeTab: 'preset',
      timeUnit,
      timeValue,
      customDateTime: defaults.customDateTime,
      activePresetId: matchingPreset ? matchingPreset.id : null,
    };
  }

  return {
    activeTab: 'custom',
    timeUnit: defaults.timeUnit,
    timeValue: defaults.timeValue,
    customDateTime: formatDateTimeLocal(expiresAt),
    activePresetId: 'custom',
  };
}

const formatFullDateTime = (date) => {
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const PublicLinkExpirySelector = ({
  onChange,
  disabled = false,
  className = '',
  initialExpiresAt = null,
  initialCreatedAt = null,
}) => {
  const initialState = useMemo(
    () => deriveInitialState(initialExpiresAt, initialCreatedAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [activeTab, setActiveTab] = useState(initialState.activeTab); // 'preset' | 'custom'
  const [timeUnit, setTimeUnit] = useState(initialState.timeUnit); // 'hours' | 'days'
  const [timeValue, setTimeValue] = useState(initialState.timeValue);
  const [customDateTime, setCustomDateTime] = useState(initialState.customDateTime);
  const [activePresetId, setActivePresetId] = useState(initialState.activePresetId);

  // Compute calculated target Date
  const computedExpiryDate = useMemo(() => {
    if (activeTab === 'custom') {
      if (!customDateTime) return null;
      const parsed = new Date(customDateTime);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    if (timeValue === '' || timeValue === null) return null;
    const val = Number(timeValue);
    if (!Number.isFinite(val) || val <= 0) return null;

    let ms = 0;
    if (timeUnit === 'hours') ms = val * 3600 * 1000;
    else if (timeUnit === 'days') ms = val * 86400 * 1000;

    return new Date(Date.now() + ms);
  }, [activeTab, customDateTime, timeUnit, timeValue]);

  // Validation
  const isValid = useMemo(() => {
    if (!computedExpiryDate) return false;
    return computedExpiryDate.getTime() > Date.now();
  }, [computedExpiryDate]);

  // Notify parent whenever computedExpiryDate changes
  useEffect(() => {
    if (onChange && computedExpiryDate && isValid) {
      onChange(computedExpiryDate.toISOString(), true);
    } else if (onChange) {
      onChange('', false);
    }
  }, [computedExpiryDate, isValid, onChange]);

  // Handle Preset quick button click
  const handleSelectPreset = (preset) => {
    if (disabled) return;
    setActivePresetId(preset.id);
    if (preset.id === 'custom') {
      setActiveTab('custom');
      // Set default custom date to current computed target or 1 hour from now
      if (!customDateTime) {
        setCustomDateTime(getMinDateTimeLocal(60 * 60 * 1000));
      }
    } else {
      setActiveTab('preset');
      setTimeUnit(preset.unit);
      setTimeValue(preset.value);
    }
  };

  // Handle unit tab switch
  const handleSelectUnit = (unit) => {
    if (disabled) return;
    setTimeUnit(unit);
    setActiveTab('preset');
    const config = UNIT_CONFIG[unit];
    const defaultVal = config?.defaultVal || 1;
    setTimeValue(defaultVal);
    // Check if matches preset
    const matchingPreset = PRESETS.find(p => p.unit === unit && p.value === defaultVal);
    setActivePresetId(matchingPreset ? matchingPreset.id : null);
  };

  // Stepper handlers
  const handleDecrement = () => {
    if (disabled) return;
    const config = UNIT_CONFIG[timeUnit] || { min: 1, step: 1 };
    const step = config.step;
    setTimeValue((prev) => {
      const current = Number(prev) || config.min;
      const next = Math.max(config.min, current - step);
      // Update matching preset if applicable
      const matchingPreset = PRESETS.find(p => p.unit === timeUnit && p.value === next);
      setActivePresetId(matchingPreset ? matchingPreset.id : null);
      return next;
    });
  };

  const handleIncrement = () => {
    if (disabled) return;
    const config = UNIT_CONFIG[timeUnit] || { max: 1000, step: 1, min: 1 };
    const step = config.step;
    setTimeValue((prev) => {
      const current = Number(prev) || config.min;
      const next = Math.min(config.max, current + step);
      // Update matching preset if applicable
      const matchingPreset = PRESETS.find(p => p.unit === timeUnit && p.value === next);
      setActivePresetId(matchingPreset ? matchingPreset.id : null);
      return next;
    });
  };

  const handleValueChange = (e) => {
    if (disabled) return;
    const val = e.target.value;
    if (val === '') {
      setTimeValue('');
      return;
    }
    const num = parseInt(val, 10);
    if (!isNaN(num)) {
      const config = UNIT_CONFIG[timeUnit] || { min: 1, max: 1000 };
      const clamped = Math.min(config.max, Math.max(config.min, num));
      setTimeValue(clamped);
      const matchingPreset = PRESETS.find(p => p.unit === timeUnit && p.value === clamped);
      setActivePresetId(matchingPreset ? matchingPreset.id : null);
    }
  };

  const handleCustomChange = (e) => {
    if (disabled) return;
    setCustomDateTime(e.target.value);
    setActiveTab('custom');
    setActivePresetId('custom');
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Quick Preset Buttons */}
      <div>
        <label className="text-[10px] font-black text-base-content/50 uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5">
          <Sparkles size={11} className="text-primary" />
          <span>Expiry Presets</span>
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-1.5">
          {PRESETS.map((preset) => {
            const isSelected = activePresetId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                disabled={disabled}
                onClick={() => handleSelectPreset(preset)}
                className={`px-2.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 border ${
                  isSelected
                    ? 'bg-primary text-primary-content border-primary shadow-sm shadow-primary/20 scale-[1.02]'
                    : 'bg-base-200/70 hover:bg-base-200 text-base-content/80 border-base-content/10 hover:border-primary/30'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {preset.id === 'custom' ? <Calendar size={12} /> : <Timer size={12} />}
                <span>{preset.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Preset Duration Controls (Visible when activeTab !== 'custom') */}
      {activeTab !== 'custom' ? (
        <div className="bg-base-200/40 rounded-2xl p-3.5 sm:p-4 border border-base-content/10 space-y-3.5">
          {/* Unit Toggle Tabs */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold text-base-content/60">Select Unit:</span>
            <div className="inline-flex p-1 rounded-xl bg-base-300/60 border border-base-content/5">
              {Object.keys(UNIT_CONFIG).map((unitKey) => {
                const isSelected = timeUnit === unitKey;
                return (
                  <button
                    key={unitKey}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleSelectUnit(unitKey)}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all capitalize ${
                      isSelected
                        ? 'bg-base-100 text-primary shadow-xs font-extrabold'
                        : 'text-base-content/60 hover:text-base-content'
                    } disabled:opacity-50`}
                  >
                    {UNIT_CONFIG[unitKey].label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stepper with Decrement / Direct Input / Increment */}
          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-xs font-bold text-base-content/70">Duration:</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={disabled || (Number(timeValue) || 0) <= (UNIT_CONFIG[timeUnit]?.min || 1)}
                onClick={handleDecrement}
                className="w-9 h-9 rounded-xl bg-base-100 border border-base-content/15 hover:border-primary/40 hover:bg-primary/10 hover:text-primary text-base-content flex items-center justify-center transition-all disabled:opacity-30 disabled:pointer-events-none shadow-xs"
                title={`Decrease ${UNIT_CONFIG[timeUnit]?.singular || 'unit'}`}
              >
                <Minus size={15} />
              </button>

              <div className="relative">
                <input
                  type="number"
                  aria-label="Duration"
                  min={UNIT_CONFIG[timeUnit]?.min || 1}
                  max={UNIT_CONFIG[timeUnit]?.max || 1000}
                  disabled={disabled}
                  value={timeValue}
                  onChange={handleValueChange}
                  className="w-20 text-center font-bold text-base py-1.5 px-2 bg-base-100 border border-base-content/15 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl outline-none transition-all text-base-content"
                />
              </div>

              <button
                type="button"
                disabled={disabled || (Number(timeValue) || 0) >= (UNIT_CONFIG[timeUnit]?.max || 1000)}
                onClick={handleIncrement}
                className="w-9 h-9 rounded-xl bg-base-100 border border-base-content/15 hover:border-primary/40 hover:bg-primary/10 hover:text-primary text-base-content flex items-center justify-center transition-all disabled:opacity-30 disabled:pointer-events-none shadow-xs"
                title={`Increase ${UNIT_CONFIG[timeUnit]?.singular || 'unit'}`}
              >
                <Plus size={15} />
              </button>

              <span className="text-xs font-bold text-base-content/60 min-w-[50px]">
                {Number(timeValue) === 1 ? UNIT_CONFIG[timeUnit]?.singular : UNIT_CONFIG[timeUnit]?.label}
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* Custom Calendar Date & Time Input (Visible ONLY when custom is active) */
        <div className="bg-base-200/40 rounded-2xl p-3.5 sm:p-4 border border-base-content/10 space-y-2">
          <div className="flex items-center justify-between">
            <label
              htmlFor="customLinkExpiryInput"
              className="text-[11px] font-bold text-base-content/70 flex items-center gap-1.5"
            >
              <Calendar size={13} className="text-primary" />
              <span>Select Custom Expiry Date & Time</span>
            </label>
            <button
              type="button"
              onClick={() => handleSelectPreset(PRESETS[0])}
              className="text-[11px] font-bold text-primary hover:underline"
            >
              Switch to Presets
            </button>
          </div>

          <input
            id="customLinkExpiryInput"
            type="datetime-local"
            min={getMinDateTimeLocal()}
            disabled={disabled}
            className="w-full px-4 py-2.5 bg-base-100 border border-base-300 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl text-sm font-medium transition-all text-base-content"
            value={customDateTime}
            onChange={handleCustomChange}
          />
        </div>
      )}

      {/* Target Expiry Live Preview Card */}
      <div className={`p-3.5 rounded-xl border transition-all ${
        isValid
          ? 'bg-primary/5 border-primary/20 text-base-content'
          : 'bg-error/5 border-error/20 text-error'
      }`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${isValid ? 'bg-primary/10 text-primary' : 'bg-error/10 text-error'}`}>
              <Clock size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-base-content/50">
                Calculated Expiry
              </p>
              <p className="text-xs font-bold text-base-content">
                {computedExpiryDate ? formatFullDateTime(computedExpiryDate) : 'Select a date'}
              </p>
            </div>
          </div>

          {isValid && computedExpiryDate && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary/15 text-primary border border-primary/20">
              Expires {formatFutureRelative(computedExpiryDate)}
            </span>
          )}
        </div>

        {!isValid && (
          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-error/15 text-xs text-error font-medium">
            <AlertCircle size={13} />
            <span>The expiry date and time must be set to a future time.</span>
          </div>
        )}
      </div>
    </div>
  );
};

PublicLinkExpirySelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  initialExpiresAt: PropTypes.string,
  initialCreatedAt: PropTypes.string,
};

export default PublicLinkExpirySelector;
